import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { isAfter } from 'date-fns'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import {
  useCreateVacationHoldMutation,
  useDeleteVacationHoldMutation,
  useLazyGetVacationHoldDetailsQuery,
  useUpdateVacationHoldMutation,
} from 'api/accounts/vacationHoldsCrud'
import { ReactComponent as VacationIcon } from 'assets/services/hold/VacationIcon.svg'
import { ReactComponent as HoldIcon } from 'assets/work-orders/HoldIcon.svg'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHConfirmDeleteButton from 'components/common/HHConfirmDeleteButton'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import LocationsAutocompleteFormField from 'components/customer-details/content/holds/LocationsAutocompleteFormField'
import { HHDateRangePicker, HHFormSelectField, HHFormTextField } from 'components/form-fields/v5'
import { getCustomerDetails } from 'middleware/actions/customers'
import { HOLD_FORM_MODES, HOLD_TYPES } from 'settings/constants/service'
import T from 'T'
import { createDateFromString, isNotInvalidDate } from 'utils/date'
import { handleError } from 'utils/error'
import { getActiveDeactivatedLocations } from 'utils/location'

import type { HoldFormMode, HoldLocation, HoldsCustomerDetails, HoldsRootState } from './types'
import type { VacationHoldApi, VacationHoldTypeApi } from 'types/api/account/vacationHold'
import type { VacationHoldBody } from 'types/api/account/vacationHoldRequests'

type HoldFormValues = {
  title: string
  type: VacationHoldTypeApi
  locations: HoldLocation[]
  dateRange: [Date | null, Date | null]
  allLocationsSelected: boolean
  active: boolean
}

type Props = {
  accountId: string
  customerDetails: HoldsCustomerDetails | null
  selectedHold?: VacationHoldApi | null
  open?: boolean
  mode?: HoldFormMode
  onClose?: () => void
  onSave?: () => void
  initialLocationId?: string | null
}

const { HOLD, SUSPENSION, VACATION } = HOLD_TYPES

const HOLD_TYPE_OPTIONS = [
  ...[
    { type: HOLD, Icon: HoldIcon },
    { type: VACATION, Icon: VacationIcon },
  ].map(({ type, Icon }) => ({
    key: type,
    value: type,
    label: (
      <Stack alignItems="center" direction="row" gap={1}>
        <SvgIcon inheritViewBox component={Icon} fontSize="small" />
        {type}
      </Stack>
    ),
  })),
  // Suspension holds only come from suspending the account; listed so the three hold origins read side by side.
  {
    key: SUSPENSION,
    value: SUSPENSION,
    disabled: true,
    label: (
      <Stack alignItems="center" direction="row" gap={1}>
        <DeleteOutlineIcon color="error" fontSize="small" />
        <ListItemText primary={SUSPENSION} secondary={T.SET_BY_SUSPENDING_THE_ACCOUNT} />
      </Stack>
    ),
  },
]

const DEFAULT_VALUES: HoldFormValues = {
  title: '',
  type: VACATION,
  locations: [],
  dateRange: [null, null],
  allLocationsSelected: false,
  active: false,
}

const DATE_RANGE_LOCALE_TEXT = { start: 'Start *', end: 'End *' }
const DIALOG_PROPS = { maxWidth: 'xs', fullWidth: true } as const
const TITLE_SX = { color: 'text.secondary' }
const ACTIONS_SX = { justifyContent: 'space-between' }
const LOCATIONS_SX = {
  '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': { height: 'unset', paddingY: '4.5px' },
  '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap' },
}
const REQUIRED_RULES = { required: T.THIS_FIELD_IS_REQUIRED }

const validateDateRange = ([startDate, endDate]: HoldFormValues['dateRange']) => {
  if (!startDate || !endDate) return T.THIS_FIELD_IS_REQUIRED
  if (isAfter(startDate, endDate)) return T.END_DATE_CANNOT_BE_BEFORE_START_DATE
  return true
}

const DATE_RANGE_RULES = { required: T.THIS_FIELD_IS_REQUIRED, validate: validateDateRange }

const toBody = ({ type, title, dateRange: [startDate, endDate], locations, active }: HoldFormValues): VacationHoldBody => ({
  type,
  title,
  startDate: startDate && isNotInvalidDate(startDate) ? startDate : undefined,
  endDate: endDate && isNotInvalidDate(endDate) ? endDate : undefined,
  locations: locations.map(({ id }) => id),
  active,
})

const HoldsFormDialog: React.FC<Props> = ({
  accountId,
  customerDetails,
  open = false,
  selectedHold = null,
  mode = HOLD_FORM_MODES.CREATE,
  onClose,
  onSave,
  initialLocationId = null,
}) => {
  const dispatch = useDispatch()
  const activeLocations: HoldLocation[] = useMemo(() => getActiveDeactivatedLocations(customerDetails).activeLocations, [customerDetails])
  const isCreateMode = mode === HOLD_FORM_MODES.CREATE
  const isUpdateMode = mode === HOLD_FORM_MODES.UPDATE
  const [create, { isLoading: isCreateLoading }] = useCreateVacationHoldMutation()
  const [update, { isLoading: isUpdateLoading }] = useUpdateVacationHoldMutation()
  const [deleteHold, { isLoading: isDeleteLoading }] = useDeleteVacationHoldMutation()
  const [getHoldDetails, { isLoading, isFetching }] = useLazyGetVacationHoldDetailsQuery()
  const selectedLocationId = useSelector((state: HoldsRootState) => state.CustomerDetails?.selectedLocationId ?? '', shallowEqual)
  const selectedHoldId = selectedHold?.id
  const isFormLoading = isLoading || isFetching
  const deleteButtonProps = useMemo(() => ({ loading: isDeleteLoading }), [isDeleteLoading])

  const {
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<HoldFormValues>({ defaultValues: DEFAULT_VALUES })
  const allLocationsSelected = watch('allLocationsSelected')
  const titleHelperText = errors.title?.message
  const dateRangeHelperText = errors.dateRange?.message
  const locationsHelperText = errors.locations?.message

  const handleClose = useCallback(() => {
    onClose?.()
    reset(DEFAULT_VALUES)
  }, [onClose, reset])

  const handleSave = useMemo(
    () =>
      handleSubmit(values => {
        const data = toBody(values)
        const request =
          isCreateMode || !selectedHoldId ? create({ data, accountId }) : update({ data, accountId, vacationHoldId: selectedHoldId })
        return request
          .unwrap()
          .then(() => {
            dispatch(getCustomerDetails({ accountId }))
            reset(DEFAULT_VALUES)
            onSave?.()
          })
          .catch(handleError)
      }),
    [accountId, create, dispatch, handleSubmit, isCreateMode, onSave, reset, selectedHoldId, update]
  )

  const handleDeleteConfirmed = useCallback(() => {
    if (selectedHoldId) {
      deleteHold({ vacationHoldId: selectedHoldId, accountId })
        .unwrap()
        .then(() => dispatch(getCustomerDetails({ accountId })))
        .catch(handleError)
    }
    handleClose()
  }, [accountId, deleteHold, dispatch, handleClose, selectedHoldId])

  useEffect(() => {
    if (!open || !selectedHoldId || isCreateMode) return
    getHoldDetails({ vacationHoldId: selectedHoldId, accountId })
      .unwrap()
      .then(hold => {
        const holdLocationIds = hold.locations ?? []
        const selectedLocations = activeLocations.filter(location => holdLocationIds.includes(location.id))
        reset({
          active: Boolean(hold.active),
          type: hold.type ?? VACATION,
          title: hold.title ?? '',
          dateRange: [createDateFromString(hold.startDate), createDateFromString(hold.endDate)],
          locations: selectedLocations,
          allLocationsSelected: activeLocations.length === selectedLocations.length && selectedLocations.length !== 0,
        })
      })
      .catch(handleError)
  }, [open, selectedHoldId, isCreateMode, accountId, activeLocations, getHoldDetails, reset])

  useEffect(() => {
    if (!open || isUpdateMode) return
    const locationIdToUse = initialLocationId || selectedLocationId
    const selectedLocation = locationIdToUse ? activeLocations.find(location => location.id === locationIdToUse) : undefined
    if (selectedLocation) {
      reset({ ...DEFAULT_VALUES, locations: [selectedLocation], allLocationsSelected: activeLocations.length === 1 })
      return
    }
    reset({ ...DEFAULT_VALUES, locations: activeLocations })
  }, [open, isUpdateMode, selectedLocationId, initialLocationId, activeLocations, reset])

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
      <HHDialogTitle sx={TITLE_SX} title={isCreateMode ? 'Add a hold' : 'Edit hold'} onClose={handleClose} />
      <DialogContent>
        <Stack justifyContent="center" spacing={3} sx={{ mt: 4, mb: 2 }}>
          <HHFormSelectField
            fullWidth
            required
            control={control}
            deprecatedLabel={false}
            disabled={isFormLoading}
            label={T.TYPE}
            name="type"
            options={HOLD_TYPE_OPTIONS}
          />
          <HHDateRangePicker
            required
            control={control}
            error={!!dateRangeHelperText}
            helperText={dateRangeHelperText}
            localeText={DATE_RANGE_LOCALE_TEXT}
            name="dateRange"
            readOnly={isFormLoading}
            rules={DATE_RANGE_RULES}
          />
          <HHFormTextField
            fullWidth
            required
            control={control}
            deprecatedLabel={false}
            error={titleHelperText}
            helperText={titleHelperText}
            label="Title"
            name="title"
            readOnly={isFormLoading}
            rules={REQUIRED_RULES}
          />
          <LocationsAutocompleteFormField
            required
            allLocationsSelected={allLocationsSelected}
            control={control}
            error={Boolean(locationsHelperText)}
            helperText={locationsHelperText}
            label="Locations"
            name="locations"
            readOnly={isFormLoading}
            rules={REQUIRED_RULES}
            setValue={setValue}
            sx={LOCATIONS_SX}
          />
        </Stack>
      </DialogContent>
      <HHDialogActions sx={ACTIONS_SX}>
        {isUpdateMode && (
          <HHConfirmDeleteButton
            confirmDescription={<Typography component="p">{T.ARE_YOU_SURE_YOU_WANT_TO_CONTINUE}</Typography>}
            confirmTitle="Confirm hold deletion"
            DeleteButtonProps={deleteButtonProps}
            DialogProps={DIALOG_PROPS}
            size="small"
            variant="outlined"
            onDeleteConfirmed={handleDeleteConfirmed}
          />
        )}
        <Stack
          alignItems="center"
          direction="row"
          justifyContent={isCreateMode ? 'flex-end' : 'space-between'}
          spacing={1}
          sx={isCreateMode ? { width: '100%' } : undefined}
        >
          <CancelButton onClick={handleClose} />
          <SaveButton loading={isCreateLoading || isUpdateLoading} onClick={handleSave} />
        </Stack>
      </HHDialogActions>
    </Dialog>
  )
}

export default HoldsFormDialog
